import { ref, computed } from 'vue';
import { computeMonthFromWeek } from '../calculations/cmgCalculations';

/**
 * All reactive state for the simulator: employer settings, contract settings,
 * wage, allowances, server data, validation, and week/month conversions.
 *
 * This is the "data layer" — equivalent to the old cmgSimulator mixin.
 */
export function useContractParams({ toFloatForDisplay }) {
  // ===== Constants =====

  const totalYearlyWeeks = 52;
  const yearlyWorkedWeeks = 47;
  const maxHoursYear = 744;
  const averageMonthlySalaryRange = [1426, 2000, 4000, 6000];

  // ===== Employer settings =====

  const situation = ref('couple');
  const children = ref('1');
  const childAge = ref('1');
  const averageMonthlySalary = ref('4000');
  const isCustomMonthlySalary = ref(false);
  const cmgAidToggle = ref('yes');
  const otherAidToggle = ref(false);
  const otherAid = ref('0');
  const handicapToggle = ref(false);
  const region = ref('0');

  // ===== Contract settings =====

  const monthWeek = ref('week');
  const weeklyWorkedDays = ref('5');
  const weeklyHours = ref('40');
  const yearlyWeeks = ref('52');
  const monthlyWorkedDays = ref('');
  const monthlyHours = ref('');
  const hasOvertime = ref(false);
  const monthlyOvertime = ref('0');
  const increaseOvertime = ref('25');
  const contractEndsBeforeOneYear = ref(false);

  // ===== Wage =====

  const grossHourlyWage = ref('4.00');
  const grossHourlyWageInput = ref('4,00');
  const maximumGrossHourlyWage = ref(15.00);
  const hourlyWageSetFromInput = ref(false);
  const isHourlyRateInRange = ref(true);

  // ===== Allowances =====

  const ie = ref('3,20');
  const maintenanceAllowanceMinimum = ref(2.65);
  const mealDailyAllowance = ref('0,00');
  const transportDailyAllowance = ref('0,00');

  // ===== Server data (set via initialize) =====

  const netRatios = ref({});
  const socialContributions = ref({});
  const minimumGrossHourlyWage = ref(0);
  const maxNetHelp = ref(8.09);

  // Raw inputs of the yearly_weeks annualisation, populated by loadFromContract.
  // Surfaced in the form so the user can see the contract's actual span next to
  // the annualised "Semaines d'accueil / an" value (only meaningful when
  // contract_ends_before_one_year is true; null otherwise).
  const schedulableWeeks = ref(null);
  const weeksWorked = ref(null);

  // ===== UI state =====

  const isPublic = ref(false);
  const loadContractLabel = ref("Charger les infos d'un contrat");
  const slider = ref(null);

  // ===== Parsed values =====

  const childrenI = computed(() => Number(children.value));
  const grossHourlyWageF = computed(() => parseFloat(grossHourlyWage.value.replace(',', '.')));
  const monthlyWorkedDaysF = computed(() => parseFloat(monthlyWorkedDays.value.replace(',', '.')));
  const monthlyHoursF = computed(() => parseFloat(monthlyHours.value.replace(',', '.')));
  const weeklyWorkedDaysF = computed(() => parseFloat(weeklyWorkedDays.value.replace(',', '.')));
  const weeklyHoursF = computed(() => parseFloat(weeklyHours.value.replace(',', '.')));
  const increaseOvertimeI = computed(() => Number(increaseOvertime.value));
  const otherAidF = computed(() => otherAidToggle.value ? parseFloat(otherAid.value.replace(',', '.')) : 0);
  const averageMonthlySalaryParsed = computed(() => parseFloat(averageMonthlySalary.value.replace(/\s/g, '').replace(',', '.')));
  const maintenanceAllowanceFloat = computed(() => parseFloat(ie.value.replace(',', '.')));
  const transportDailyAllowanceParsed = computed(() => parseFloat(transportDailyAllowance.value.replace(',', '.')));
  const mealDailyAllowanceParsed = computed(() => parseFloat(mealDailyAllowance.value.replace(',', '.')));

  const yearlyWeeksF = computed(() => {
    const result = parseFloat(yearlyWeeks.value.replace(',', '.'));
    return contractEndsBeforeOneYear.value ? result : Math.floor(result);
  });

  // ===== Net ratios =====

  const netRatioForRegion = computed(() => netRatios.value[region.value] || { label: '', classic: 0, detax: 0 });
  const netRatioClassic = computed(() => netRatioForRegion.value.classic);
  const netRatioDetax = computed(() => netRatioForRegion.value.detax);
  const regionFrench = computed(() => netRatioForRegion.value.label);

  // ===== Situation =====

  const childTooOld = computed(() => situation.value !== 'single' ? parseInt(childAge.value) > 2 : childAge.value === '4');
  const cmgNotAvailable = computed(() => childTooOld.value || cmgAidToggle.value === 'no');
  const cmgUnavailable = computed(() => childTooOld.value || cmgAidToggle.value === 'no');
  const situationFrench = computed(() => situation.value === 'single' ? 'parent seul' : 'en couple');
  const childAgeFrench = computed(() => ({
    1: 'moins de 3 ans', 2: '3 à 5 ans', 3: '6 à 11 ans', 4: '12 ans ou plus',
  }[childAge.value]));
  const handicapToggleInfos = computed(() => {
    return handicapToggle.value
      ? '<p>Le montant est majoré de 30% quand votre enfant est en situation d\'handicap.</p>'
      : '';
  });
  const averageMonthlySalaries = computed(() => {
    return isCustomMonthlySalary.value ? [averageMonthlySalaryParsed.value] : averageMonthlySalaryRange;
  });

  // ===== Overtime =====

  const weeklyOvertimeFloat = computed(() => hasOvertime.value ? weeklyHoursF.value - 45 : 0);
  const monthlyOvertimeFloat = computed(() => {
    return isMonth.value
      ? parseFloat(monthlyOvertime.value.replace(',', '.'))
      : (weeklyOvertimeFloat.value / weeklyWorkedDaysF.value * monthlyWorkedDaysF.value);
  });

  // ===== Validation =====

  const isWeeklyWorkedDays = computed(() => weeklyWorkedDays.value !== '' && weeklyWorkedDaysF.value >= 0 && weeklyWorkedDaysF.value <= 7);
  const isWeeklyHours = computed(() => weeklyHours.value !== '' && weeklyHoursF.value >= 0 && weeklyHoursF.value <= 75);
  const isYearlyWeeks = computed(() => {
    return yearlyWeeks.value !== '' &&
      (yearlyWeeksF.value === 52 ||
        (yearlyWeeksF.value >= 0 &&
          (yearlyWeeksF.value <= 46 ||
            (contractEndsBeforeOneYear.value && yearlyWeeksF.value < 52)
          )
        )
      );
  });
  const isMonthlyHours = computed(() => monthlyHours.value !== '' && monthlyHoursF.value >= 0 && monthlyHoursF.value <= maxHoursYear);
  const isMonthlyWorkedDays = computed(() => monthlyWorkedDays.value !== '' && monthlyWorkedDaysF.value >= 0 && monthlyWorkedDaysF.value <= 31);
  const isMonthlyOvertime = computed(() => hasOvertime.value && monthlyOvertime.value !== '' && monthlyOvertimeFloat.value >= 0 && monthlyOvertimeFloat.value <= 140);
  const isMaintenanceAllowanceAllowed = computed(() => ie.value !== '' && maintenanceAllowanceFloat.value >= 0 && maintenanceAllowanceFloat.value <= 50);
  const isFullYear = computed(() => parseInt(yearlyWeeks.value) === totalYearlyWeeks);
  const isWeek = computed(() => monthWeek.value === 'week');
  const isMonth = computed(() => monthWeek.value === 'month');

  const isCalculable = computed(() => {
    if (monthWeek.value === 'month') {
      if (!isMonthlyHours.value || !isMonthlyWorkedDays.value) return false;
      if (hasOvertime.value && !isMonthlyOvertime.value) return false;
      if (hasOvertime.value && (monthlyOvertimeFloat.value > monthlyHoursF.value)) return false;
      if (!isWeeklyHours.value || !isWeeklyWorkedDays.value || !isYearlyWeeks.value) return false;
    } else if (monthWeek.value === 'week') {
      if (!isWeeklyHours.value || !isWeeklyWorkedDays.value || !isYearlyWeeks.value) return false;
    }
    if (monthWeek.value === 'other') return false;
    return isMaintenanceAllowanceAllowed.value;
  });

  // ===== Wage computeds =====

  const regularHours = computed(() => monthlyHoursF.value - (hasOvertime.value ? monthlyOvertimeFloat.value : 0));
  const grossMonthlyWageRegular = computed(() => regularHours.value * grossHourlyWageF.value);
  const grossMonthlyOvertimeWage = computed(() => hasOvertime.value ? monthlyOvertimeFloat.value * grossHourlyWageF.value : 0);
  const grossMonthlyOvertimeWagePremium = computed(() => {
    return hasOvertime.value ? monthlyOvertimeFloat.value * grossHourlyWageF.value * increaseOvertimeI.value / 100 : 0;
  });
  const netHourlyWage = computed(() => grossHourlyWageF.value * netRatioClassic.value);

  // ===== Allowance computeds =====

  const allowanceDays = computed(() => {
    return isFullYear.value
      ? monthlyWorkedDaysF.value * yearlyWorkedWeeks / 52
      : monthlyWorkedDaysF.value;
  });
  const maintenanceAllowanceMonthly = computed(() => maintenanceAllowanceFloat.value * allowanceDays.value);
  const maintenanceAllowanceHourly = computed(() => maintenanceAllowanceMonthly.value / monthlyHoursF.value);
  const maintenanceAllowanceMonthlyDetaxLimit = computed(() => {
    return Math.min(maintenanceAllowanceFloat.value, maintenanceAllowanceMinimum.value) * allowanceDays.value;
  });
  const transportAllowanceMonthly = computed(() => transportDailyAllowanceParsed.value * allowanceDays.value);
  const mealAllowanceMonthly = computed(() => mealDailyAllowanceParsed.value * allowanceDays.value);
  const mealAllowanceHourly = computed(() => mealAllowanceMonthly.value / monthlyHoursF.value);

  // ===== Week/Month conversion =====

  function monthFromWeek() {
    const result = computeMonthFromWeek({
      weeklyWorkedDays: weeklyWorkedDaysF.value,
      weeklyHours: weeklyHoursF.value,
      yearlyWeeks: yearlyWeeksF.value,
      hasOvertime: hasOvertime.value,
      weeklyOvertimeFloat: weeklyOvertimeFloat.value,
    });

    monthlyWorkedDays.value = isWeeklyWorkedDays.value && isYearlyWeeks.value
      ? toFloatForDisplay(result.monthlyWorkedDays, 4)
      : '';
    monthlyHours.value = isWeeklyHours.value && yearlyWeeks.value
      ? toFloatForDisplay(result.monthlyHours, 4)
      : '';

    if (hasOvertime.value) {
      monthlyOvertime.value = isNaN(result.monthlyOvertime) ? '0' : toFloatForDisplay(result.monthlyOvertime, 4);
    } else {
      monthlyOvertime.value = '0';
    }
  }

  function weekFromMonth() {
    if (isMonthlyOvertime.value) {
      weeklyHours.value = '40';
      let weeklyOvertime = 0;
      if (isWeeklyWorkedDays.value && isMonthlyWorkedDays.value) {
        weeklyOvertime = ((monthlyOvertimeFloat.value / monthlyWorkedDaysF.value) * weeklyWorkedDaysF.value);
      }
      if (isMonthlyHours.value && isWeeklyWorkedDays.value && isMonthlyWorkedDays.value) {
        const normalWorkingHours = monthlyHoursF.value - monthlyOvertimeFloat.value;
        weeklyHours.value = toFloatForDisplay(
          ((normalWorkingHours / monthlyWorkedDaysF.value * weeklyWorkedDaysF.value) + weeklyOvertime), 2);
      }
    } else {
      if (isMonthlyHours.value && isWeeklyWorkedDays.value && isMonthlyWorkedDays.value) {
        weeklyHours.value = toFloatForDisplay((monthlyHoursF.value * 12 / yearlyWeeksF.value), 2);
        weeklyWorkedDays.value = toFloatForDisplay(monthlyWorkedDaysF.value * 12 / yearlyWeeksF.value, 2);
      } else {
        weeklyHours.value = '40';
      }
    }
  }

  function toggleMW() {
    if (monthWeek.value === 'month') monthFromWeek();
    if (monthWeek.value === 'week') weekFromMonth();
  }

  // ===== Slider helpers =====

  function setGrossHourlyWage(value) {
    grossHourlyWage.value = value;
    grossHourlyWageInput.value = value;
    // Update the noUiSlider if it exists
    if (slider.value) {
      const numericValue = parseFloat(value.replace(',', '.'));
      if (!isNaN(numericValue)) slider.value.set(numericValue);
    }
  }

  // Slider drag write-back. The slider already shows `numericValue`, so
  // calling `slider.set()` here would re-trigger noUiSlider's `update`
  // event and double the per-pixel reactive cost (visible as drag lag).
  function setGrossHourlyWageFromSlider(numericValue) {
    const formatted = toFloatForDisplay(numericValue, 2);
    grossHourlyWage.value = formatted;
    grossHourlyWageInput.value = formatted;
  }

  function adjustHourlyRate(adjustment) {
    let hourlyWage = grossHourlyWageF.value;
    hourlyWage = adjustment === 'increase' ? hourlyWage + 0.01 : hourlyWage - 0.01;
    if (slider.value) slider.value.set(hourlyWage);
    const formatted = toFloatForDisplay(hourlyWage, 2);
    grossHourlyWage.value = formatted;
    grossHourlyWageInput.value = formatted;
  }

  function updateHourlyWage() {
    hourlyWageSetFromInput.value = true;
    const hourlyWage = parseFloat(grossHourlyWageInput.value.replace(',', '.'));
    if (hourlyWage <= maximumGrossHourlyWage.value || hourlyWage >= minimumGrossHourlyWage.value) {
      grossHourlyWage.value = toFloatForDisplay(hourlyWage, 2);
      if (slider.value) slider.value.set(hourlyWage);
    }
    hourlyWageSetFromInput.value = false;
  }

  // ===== Error checking =====

  function checkErrors() {
    const toggleInvalid = (selector, isInvalid) => {
      document.querySelectorAll(selector).forEach(el => el.classList.toggle('is-invalid', isInvalid));
    };
    toggleInvalid('.wrapper_input.simulator_weekly_worked_days .input-group', !isWeeklyWorkedDays.value);
    toggleInvalid('.wrapper_input.simulator_weekly_hours .input-group', !isWeeklyHours.value);
    toggleInvalid('.wrapper_input.simulator_yearly_weeks .input-group', !isYearlyWeeks.value);
    toggleInvalid('.wrapper_input.simulator_monthly_worked_days .input-group', !isMonthlyWorkedDays.value);
    toggleInvalid('.wrapper_input.simulator_monthly_hours .input-group', !isMonthlyHours.value);
    toggleInvalid('.wrapper_input.simulator_daily_maintenance_allowance .input-group', !isMaintenanceAllowanceAllowed.value);
    toggleInvalid('.wrapper_input.simulator_monthly_overtime .input-group', !isMonthlyOvertime.value);
  }

  function addBlur() {
    document.querySelectorAll('.blur').forEach(el => el.classList.add('blurring'));
  }

  function print() {
    // Dynamic import to keep the print helper out of the initial bundle —
    // matches the lazy-load behavior the legacy `import('components/print')`
    // call had under Shakapacker.
    import('./printSection.js').then((module) =>
      module.printPageSection('#cmg-simulator__print', ['cmg-simulator']),
    );
  }

  // ===== Initialize / Load =====

  function initialize(apiData) {
    netRatios.value = apiData.net_ratios;
    socialContributions.value = apiData.social_contributions;
    minimumGrossHourlyWage.value = apiData.minimum_gross_hourly_wage;
    if (apiData.max_net_help) maxNetHelp.value = apiData.max_net_help;
  }

  function loadFromContract(data) {
    childAge.value = data.child_age;
    region.value = data.region;
    contractEndsBeforeOneYear.value = data.contract_ends_before_one_year;
    yearlyWeeks.value = data.yearly_weeks;
    schedulableWeeks.value = data.schedulable_weeks;
    weeksWorked.value = data.weeks_worked;
    monthWeek.value = data.month_week;
    hasOvertime.value = data.has_overtime;
    loadContractLabel.value = data.load_contract_label;
    ie.value = data.ie;
    mealDailyAllowance.value = data.meal_daily_allowance;

    setTimeout(() => {
      monthlyWorkedDays.value = data.monthly_worked_days;
      monthlyHours.value = data.monthly_hours;
      monthlyOvertime.value = data.monthly_overtime;
      setGrossHourlyWage(data.gross_hourly_wage);
    });
  }

  return {
    // Employer settings
    situation,
    children,
    childAge,
    averageMonthlySalary,
    isCustomMonthlySalary,
    cmgAidToggle,
    otherAidToggle,
    otherAid,
    handicapToggle,
    region,

    // Contract settings
    monthWeek,
    weeklyWorkedDays,
    weeklyHours,
    yearlyWeeks,
    monthlyWorkedDays,
    monthlyHours,
    hasOvertime,
    monthlyOvertime,
    increaseOvertime,
    contractEndsBeforeOneYear,

    // Wage
    grossHourlyWage,
    grossHourlyWageInput,
    maximumGrossHourlyWage,
    hourlyWageSetFromInput,
    isHourlyRateInRange,

    // Allowances
    ie,
    maintenanceAllowanceMinimum,
    mealDailyAllowance,
    transportDailyAllowance,

    // Server data
    netRatios,
    socialContributions,
    minimumGrossHourlyWage,
    maxNetHelp,
    schedulableWeeks,
    weeksWorked,

    // UI
    isPublic,
    loadContractLabel,
    slider,

    // Parsed values
    childrenI,
    grossHourlyWageF,
    monthlyWorkedDaysF,
    monthlyHoursF,
    weeklyWorkedDaysF,
    weeklyHoursF,
    increaseOvertimeI,
    otherAidF,
    averageMonthlySalaryParsed,
    maintenanceAllowanceFloat,
    transportDailyAllowanceParsed,
    mealDailyAllowanceParsed,
    yearlyWeeksF,

    // Net ratios
    netRatioForRegion,
    netRatioClassic,
    netRatioDetax,
    regionFrench,

    // Situation
    childTooOld,
    cmgNotAvailable,
    cmgUnavailable,
    situationFrench,
    childAgeFrench,
    handicapToggleInfos,
    averageMonthlySalaries,

    // Overtime
    weeklyOvertimeFloat,
    monthlyOvertimeFloat,

    // Validation
    isWeeklyWorkedDays,
    isWeeklyHours,
    isYearlyWeeks,
    isMonthlyHours,
    isMonthlyWorkedDays,
    isMonthlyOvertime,
    isMaintenanceAllowanceAllowed,
    isFullYear,
    isWeek,
    isMonth,
    isCalculable,

    // Wage computeds
    regularHours,
    grossMonthlyWageRegular,
    grossMonthlyOvertimeWage,
    grossMonthlyOvertimeWagePremium,
    netHourlyWage,

    // Allowance computeds
    allowanceDays,
    maintenanceAllowanceMonthly,
    maintenanceAllowanceHourly,
    maintenanceAllowanceMonthlyDetaxLimit,
    transportAllowanceMonthly,
    mealAllowanceMonthly,
    mealAllowanceHourly,

    // Methods
    monthFromWeek,
    weekFromMonth,
    toggleMW,
    setGrossHourlyWage,
    setGrossHourlyWageFromSlider,
    adjustHourlyRate,
    updateHourlyWage,
    checkErrors,
    addBlur,
    print,
    initialize,
    loadFromContract,
  };
}
